import { useCallback, useRef, useState } from 'react';
import type { KeyboardEvent, PointerEvent } from 'react';
import { PAGE_TREE_WIDTH_MAX, PAGE_TREE_WIDTH_MIN, PAGE_TREE_WIDTH_STEP, clampPageTreeWidth } from './pageTreeWidth';

export interface SubNavResizeProps {
    width: number;
    /** The width settled: a drag ended, or a key moved it. */
    onChange: (width: number) => void;
    /** Double-click: back to the default width. */
    onReset: () => void;
    /** Accessible name. Default "Resize the pages column". */
    label?: string;
}

/**
 * The drag handle on the right edge of the pages column: pointer drag (mouse, pen, touch), arrow keys by 16 px
 * (Shift: 64 px), Home / End to the limits, double-click to the default. A vertical `separator` whose value is the
 * width. While dragging, the width is written straight to the column's `--ark-subnav-w` (the parent element) and
 * `onChange` fires once at the end, so the shell does not re-render on every pointer move.
 */
export function SubNavResize(props: SubNavResizeProps) {
    const { width, onChange, onReset } = props;
    const [dragging, setDragging] = useState(false);
    const drag = useRef<{ x: number; width: number; column: HTMLElement | null; last: number } | null>(null);

    const onPointerDown = useCallback((event: PointerEvent<HTMLDivElement>) => {
        if (event.pointerType === 'mouse' && event.button !== 0) return;
        const handle = event.currentTarget;
        drag.current = { x: event.clientX, width, column: handle.parentElement, last: width };
        if (typeof handle.setPointerCapture === 'function') handle.setPointerCapture(event.pointerId);
        setDragging(true);
        document.body.classList.add('ark-is-resizing');
        event.preventDefault();
    }, [width]);

    const onPointerMove = useCallback((event: PointerEvent<HTMLDivElement>) => {
        const state = drag.current;
        if (!state) return;
        state.last = clampPageTreeWidth(state.width + (event.clientX - state.x));
        state.column?.style.setProperty('--ark-subnav-w', `${state.last}px`);
    }, []);

    const end = useCallback((event: PointerEvent<HTMLDivElement>) => {
        const state = drag.current;
        if (!state) return;
        drag.current = null;
        setDragging(false);
        document.body.classList.remove('ark-is-resizing');
        const handle = event.currentTarget;
        if (typeof handle.hasPointerCapture === 'function' && handle.hasPointerCapture(event.pointerId)) {
            handle.releasePointerCapture(event.pointerId);
        }
        if (state.last !== state.width) onChange(state.last);
    }, [onChange]);

    const onKeyDown = useCallback((event: KeyboardEvent<HTMLDivElement>) => {
        const step = event.shiftKey ? PAGE_TREE_WIDTH_STEP * 4 : PAGE_TREE_WIDTH_STEP;
        switch (event.key) {
            case 'ArrowRight':
            case 'ArrowUp':
                onChange(width + step);
                break;
            case 'ArrowLeft':
            case 'ArrowDown':
                onChange(width - step);
                break;
            case 'Home':
                onChange(PAGE_TREE_WIDTH_MIN);
                break;
            case 'End':
                onChange(PAGE_TREE_WIDTH_MAX);
                break;
            default:
                return;
        }
        event.preventDefault();
    }, [onChange, width]);

    return (
        <div
            className={dragging ? 'ark-subnav-resize is-resizing' : 'ark-subnav-resize'}
            role="separator"
            aria-orientation="vertical"
            aria-label={props.label ?? 'Resize the pages column'}
            aria-valuemin={PAGE_TREE_WIDTH_MIN}
            aria-valuemax={PAGE_TREE_WIDTH_MAX}
            aria-valuenow={width}
            tabIndex={0}
            title="Drag to resize the pages column; double-click to reset"
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={end}
            onPointerCancel={end}
            onKeyDown={onKeyDown}
            onDoubleClick={onReset}
        />
    );
}
